import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar,
  Filter,
  RefreshCw,
  Clock,
  Check,
  AlertTriangle,
  Eye,
  Edit2,
} from 'lucide-react';
import { api, type SMCCCalendarItem } from '../../../services/api';

// Platform colors for visual identification
const PLATFORM_COLORS: Record<string, string> = {
  instagram: '#E4405F',
  tiktok: '#000000',
  twitter: '#1DA1F2',
  facebook: '#1877F2',
  pinterest: '#BD081C',
  youtube: '#FF0000',
  linkedin: '#0A66C2',
};

const STATUS_CONFIG: Record<string, { color: string; bg: string; label: string }> = {
  draft: { color: 'text-gray-600', bg: 'bg-gray-100', label: 'Draft' },
  pending_approval: { color: 'text-amber-600', bg: 'bg-amber-100', label: 'Pending' },
  approved: { color: 'text-blue-600', bg: 'bg-blue-100', label: 'Approved' },
  scheduled: { color: 'text-purple-600', bg: 'bg-purple-100', label: 'Scheduled' },
  publishing: { color: 'text-indigo-600', bg: 'bg-indigo-100', label: 'Publishing' },
  published: { color: 'text-green-600', bg: 'bg-green-100', label: 'Published' },
  failed: { color: 'text-red-600', bg: 'bg-red-100', label: 'Failed' },
  archived: { color: 'text-gray-500', bg: 'bg-gray-100', label: 'Archived' },
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];


function CalendarEventCard({
  item,
  onClick,
}: {
  item: SMCCCalendarItem;
  onClick: () => void;
}) {
  const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.draft;
  const mainPlatform = item.target_platforms[0];
  const platformColor = PLATFORM_COLORS[mainPlatform] || '#666';

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left mb-1 p-1.5 rounded text-xs cursor-pointer hover:opacity-80 transition-opacity"
      style={{ backgroundColor: `${platformColor}15`, borderLeft: `3px solid ${platformColor}` }}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="font-medium truncate flex-1" title={item.title || 'Untitled'}>
          {item.title || 'Untitled'}
        </span>
        <span className={`px-1 py-0.5 rounded text-[10px] font-medium ${statusConfig.bg} ${statusConfig.color}`}>
          {statusConfig.label}
        </span>
      </div>
      <div className="flex items-center gap-1 mt-0.5 text-gray-500">
        <Clock className="w-3 h-3" />
        <span>
          {new Date(item.scheduled_at || item.published_at || '').toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
        {item.target_platforms.length > 1 && (
          <span className="text-[10px]">+{item.target_platforms.length - 1}</span>
        )}
      </div>
    </button>
  );
}

function DayCell({
  date,
  items,
  isToday,
  isCurrentMonth,
  onItemClick,
  onAddClick,
}: {
  date: Date;
  items: SMCCCalendarItem[];
  isToday: boolean;
  isCurrentMonth: boolean;
  onItemClick: (item: SMCCCalendarItem) => void;
  onAddClick: (date: Date) => void;
}) {
  const maxVisible = 3;
  const visibleItems = items.slice(0, maxVisible);
  const hiddenCount = items.length - maxVisible;

  return (
    <div
      className={`min-h-[120px] border-b border-r border-gray-200 p-1 ${
        isCurrentMonth ? 'bg-white' : 'bg-gray-50'
      } ${isToday ? 'ring-2 ring-inset ring-primary-500' : ''}`}
    >
      <div className="flex items-center justify-between mb-1">
        <span
          className={`text-sm font-medium ${
            isToday
              ? 'bg-primary-600 text-white w-6 h-6 rounded-full flex items-center justify-center'
              : isCurrentMonth
              ? 'text-gray-900'
              : 'text-gray-400'
          }`}
        >
          {date.getDate()}
        </span>
        <button
          onClick={() => onAddClick(date)}
          className="p-0.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="space-y-0.5">
        {visibleItems.map((item) => (
          <CalendarEventCard key={item.id} item={item} onClick={() => onItemClick(item)} />
        ))}
        {hiddenCount > 0 && (
          <button className="text-[10px] text-gray-500 hover:text-primary-600 w-full text-left px-1">
            +{hiddenCount} more
          </button>
        )}
      </div>
    </div>
  );
}

export default function SMCCCalendar() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedItem, setSelectedItem] = useState<SMCCCalendarItem | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [platformFilter, setPlatformFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  // Calculate date range for current month view
  const dateRange = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);

    // Adjust to include days from previous/next month to fill the grid
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));

    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0],
      firstDay,
      lastDay,
      startDate,
      endDate,
    };
  }, [currentDate]);

  // Fetch calendar data
  const { data: calendarData, isLoading, refetch } = useQuery({
    queryKey: ['smcc-calendar', dateRange.start, dateRange.end, platformFilter, statusFilter],
    queryFn: () =>
      api.getSMCCCalendar({
        start_date: dateRange.start,
        end_date: dateRange.end,
        platform: platformFilter.length > 0 ? platformFilter : undefined,
        status: statusFilter.length > 0 ? statusFilter : undefined,
      }),
  });

  // Generate calendar grid
  const calendarDays = useMemo(() => {
    const days: Array<{ date: Date; items: SMCCCalendarItem[] }> = [];
    const current = new Date(dateRange.startDate);

    while (current <= dateRange.endDate) {
      const dateKey = current.toISOString().split('T')[0];
      const items = calendarData?.by_date?.[dateKey] || [];
      days.push({
        date: new Date(current),
        items,
      });
      current.setDate(current.getDate() + 1);
    }

    return days;
  }, [dateRange, calendarData]);

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleItemClick = (item: SMCCCalendarItem) => {
    setSelectedItem(item);
  };

  const handleAddClick = (date: Date) => {
    // Navigate to content editor with pre-filled date
    navigate(`/admin/smcc/content/new?scheduled_at=${date.toISOString()}`);
  };

  const today = new Date();
  const isToday = (date: Date) =>
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  const isCurrentMonth = (date: Date) => date.getMonth() === currentDate.getMonth();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-900">Content Calendar</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={goToPreviousMonth}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-lg font-semibold text-gray-900 min-w-[180px] text-center">
              {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
            </span>
            <button
              onClick={goToNextMonth}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={goToToday}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Today
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg border ${
              showFilters || platformFilter.length > 0 || statusFilter.length > 0
                ? 'bg-primary-50 border-primary-200 text-primary-600'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-5 h-5" />
          </button>
          <button
            onClick={() => refetch()}
            className="p-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => navigate('/admin/smcc/content/new')}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Content
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-2">Platforms</span>
              <div className="flex flex-wrap gap-2">
                {Object.entries(PLATFORM_COLORS).map(([platform, color]) => (
                  <button
                    key={platform}
                    onClick={() =>
                      setPlatformFilter((prev) =>
                        prev.includes(platform)
                          ? prev.filter((p) => p !== platform)
                          : [...prev, platform]
                      )
                    }
                    className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                      platformFilter.includes(platform)
                        ? 'text-white border-transparent'
                        : 'text-gray-700 border-gray-300 hover:border-gray-400'
                    }`}
                    style={{
                      backgroundColor: platformFilter.includes(platform) ? color : 'transparent',
                    }}
                  >
                    {platform}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <span className="block text-sm font-medium text-gray-700 mb-2">Status</span>
              <div className="flex flex-wrap gap-2">
                {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                  <button
                    key={status}
                    onClick={() =>
                      setStatusFilter((prev) =>
                        prev.includes(status)
                          ? prev.filter((s) => s !== status)
                          : [...prev, status]
                      )
                    }
                    className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                      statusFilter.includes(status)
                        ? `${config.bg} ${config.color} border-transparent`
                        : 'text-gray-700 border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {config.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {(platformFilter.length > 0 || statusFilter.length > 0) && (
            <button
              onClick={() => {
                setPlatformFilter([]);
                setStatusFilter([]);
              }}
              className="mt-3 text-sm text-primary-600 hover:text-primary-700"
            >
              Clear all filters
            </button>
          )}
        </div>
      )}

      {/* Calendar Grid */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-gray-200">
          {DAYS.map((day) => (
            <div
              key={day}
              className="py-2 text-center text-sm font-semibold text-gray-600 bg-gray-50"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        {isLoading ? (
          <div className="h-[600px] flex items-center justify-center">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-7">
            {calendarDays.map(({ date, items }, index) => (
              <div key={index} className="group">
                <DayCell
                  date={date}
                  items={items}
                  isToday={isToday(date)}
                  isCurrentMonth={isCurrentMonth(date)}
                  onItemClick={handleItemClick}
                  onAddClick={handleAddClick}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-500 mb-1">
            <Calendar className="w-4 h-4" />
            <span className="text-sm">Total Scheduled</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {calendarData?.items?.filter((i) => i.status === 'scheduled').length || 0}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-500 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-sm">Pending Approval</span>
          </div>
          <p className="text-2xl font-bold text-amber-600">
            {calendarData?.items?.filter((i) => i.status === 'pending_approval').length || 0}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-500 mb-1">
            <Check className="w-4 h-4" />
            <span className="text-sm">Published This Month</span>
          </div>
          <p className="text-2xl font-bold text-green-600">
            {calendarData?.items?.filter((i) => i.status === 'published').length || 0}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-500 mb-1">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm">Failed</span>
          </div>
          <p className="text-2xl font-bold text-red-600">
            {calendarData?.items?.filter((i) => i.status === 'failed').length || 0}
          </p>
        </div>
      </div>

      {/* Item Detail Modal */}
      {selectedItem && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setSelectedItem(null)}
            onKeyDown={(e) => e.key === 'Escape' && setSelectedItem(null)}
            role="button"
            tabIndex={0}
            aria-label="Close modal"
          />
          <div className="fixed inset-y-0 right-0 w-96 bg-white shadow-xl z-50 overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Content Details</h3>
              <button
                onClick={() => setSelectedItem(null)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                &times;
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 text-lg">
                  {selectedItem.title || 'Untitled'}
                </h4>
                <span
                  className={`mt-1 inline-block px-2 py-0.5 rounded text-xs font-medium ${
                    STATUS_CONFIG[selectedItem.status]?.bg || 'bg-gray-100'
                  } ${STATUS_CONFIG[selectedItem.status]?.color || 'text-gray-600'}`}
                >
                  {STATUS_CONFIG[selectedItem.status]?.label || selectedItem.status}
                </span>
              </div>

              <div>
                <span className="text-xs text-gray-500">Content Type</span>
                <p className="font-medium text-gray-900 capitalize">{selectedItem.content_type}</p>
              </div>

              <div>
                <span className="text-xs text-gray-500">Platforms</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedItem.target_platforms.map((platform) => (
                    <span
                      key={platform}
                      className="px-2 py-0.5 text-xs font-medium rounded text-white capitalize"
                      style={{ backgroundColor: PLATFORM_COLORS[platform] || '#666' }}
                    >
                      {platform}
                    </span>
                  ))}
                </div>
              </div>

              {selectedItem.scheduled_at && (
                <div>
                  <span className="text-xs text-gray-500">Scheduled For</span>
                  <p className="font-medium text-gray-900">
                    {new Date(selectedItem.scheduled_at).toLocaleString()}
                  </p>
                </div>
              )}

              {selectedItem.published_at && (
                <div>
                  <span className="text-xs text-gray-500">Published At</span>
                  <p className="font-medium text-gray-900">
                    {new Date(selectedItem.published_at).toLocaleString()}
                  </p>
                </div>
              )}

              {selectedItem.campaign_name && (
                <div>
                  <span className="text-xs text-gray-500">Campaign</span>
                  <p className="font-medium text-gray-900">{selectedItem.campaign_name}</p>
                </div>
              )}

              <div>
                <span className="text-xs text-gray-500">Automation</span>
                <p className="font-medium text-gray-900 capitalize">
                  {selectedItem.automation_level.replace('_', ' ')}
                </p>
              </div>

              <div className="pt-4 flex gap-2">
                <button
                  onClick={() => navigate(`/admin/smcc/content/${selectedItem.id}`)}
                  className="flex-1 px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  View
                </button>
                <button
                  onClick={() => navigate(`/admin/smcc/content/${selectedItem.id}/edit`)}
                  className="flex-1 px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
